import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import {
  catchError,
  combineLatest,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  AttendanceStats,
  attendancePercent,
} from '../../../../models/attendance.school.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { UserModel } from '../../../../models/user.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { AcademicYearService } from '../../../../services/academic-year.school.service';
import { SchoolAttendanceService } from '../../../../services/attendance.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as gradeActions from '../../../../state/actions/grade.school.actions';
import * as teacherActions from '../../../../state/actions/teacher.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as teacherSelector from '../../../../state/selectors/teacher.school.selector';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { ProgressDeterminateComponent } from '../../../components/progress/progress-determinate/progress-determinate.component';

@Component({
  selector: 'app-overview.grade',
  standalone: true,
  imports: [
    AsyncPipe,
    RouterLink,
    BtnMoreComponent,
    InitialsPipe,
    ProgressDeterminateComponent,
  ],
  templateUrl: './overview.grade.component.html',
  styleUrl: './overview.grade.component.css',
})
export class OverviewGradeComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  store: Store<AppState> = inject(Store<AppState>);
  attendanceService: SchoolAttendanceService = inject(SchoolAttendanceService);
  utilsService: UtilsService = inject(UtilsService);
  logger: NGXLogger = inject(NGXLogger);
  yearService: AcademicYearService = inject(AcademicYearService);

  loading$: Observable<boolean> = new Observable();
  inProgress$: Observable<boolean> = new Observable();
  loadingStudents$: Observable<boolean> = new Observable();
  loadingAttendance$: Observable<boolean> = new Observable();

  attendanceMap: { [studentId: string]: number } = {};

  groupId: string | null = this.route.root.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.root.snapshot.paramMap.get('facultyId');
  gradeId: string | null = this.route.root.snapshot.paramMap.get('gradeId');
  academicYear: number | undefined = undefined;

  user$: Observable<UserModel | null | undefined> = new Observable();
  group: Group | null | undefined = undefined;
  grade$: Observable<Grade | null | undefined> = new Observable();
  students$: Observable<SchoolMember[] | null | undefined> = new Observable();

  myRoles$: Observable<any> = new Observable();
  exit$: Subject<void> = new Subject<void>();

  private params$ = combineLatest({
    params: this.route.paramMap,
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      let pMap = params;
      let currentRoute = this.route;

      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        gradeId: pMap.get('gradeId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.loading$ = this.store
      .pipe(select(gradeSelector.loading))
      .pipe(takeUntil(this.exit$));

    this.inProgress$ = this.store
      .pipe(select(gradeSelector.inProgress))
      .pipe(takeUntil(this.exit$));
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit(): void {
    this.params$.subscribe(({ groupId, facultyId, gradeId, academicYear }) => {
      this.groupId = groupId;
      this.facultyId = facultyId;
      this.gradeId = gradeId;
      this.academicYear = academicYear;

      this.logger.debug(
        `OverviewGradeComponent :: Received route params groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, academicYear=${academicYear}`,
      );

      if (groupId) {
        this.loadData(groupId, facultyId, gradeId, academicYear);
      }

      this.myRoles$ = this.store
        .select(groupSelector.getMyRolesInGroup(this.groupId))
        .pipe(takeUntil(this.exit$));
    });

    this.user$ = this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$));
  }

  private loadData(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    academicYear?: number,
  ) {
    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        const effectiveYear = academicYear ?? group?.academicYear;
        this.academicYear = effectiveYear;

        this.logger.debug(
          `OverviewGradeComponent :: Loaded group ${group?.name} with academicYear=${group?.academicYear}. Effective academic year for sessions: ${effectiveYear}`,
        );

        if (effectiveYear) {
          this.initDataStreams(groupId, facultyId, gradeId, effectiveYear);
        }
      });
  }

  private initDataStreams(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    academicYear: number,
  ) {
    if (gradeId) {
      this.grade$ = this.store.select(
        gradeSelector.getGradeById({ gradeId, groupId, academicYear }),
      );
    } else if (facultyId) {
      this.grade$ = this.store
        .select(
          gradeSelector.getGradeByTeacherId({
            teacherId: facultyId,
            groupId,
            academicYear,
          }),
        )
        .pipe(take(1));
    }

    this.students$ = this.grade$.pipe(
      switchMap((grade) => {
        if (!grade) return of([]); // Return empty if no grade

        // Past years: students have moved on, so the roster comes from school_archives
        if (this.isPastYear) {
          return from(
            this.yearService.getArchivedStudents(groupId, grade.id!, academicYear),
          ).pipe(
            map(
              (archives) =>
                archives.map((a) => ({
                  id: a.id,
                  groupId: a.groupId,
                  memberId: a.memberId,
                  name: a.name ?? undefined,
                  photoURL: a.photoURL ?? undefined,
                })) as unknown as SchoolMember[],
            ),
            catchError((error) => {
              this.logger.error(
                `OverviewGradeComponent :: archived students: ${error}`,
              );
              return of([] as SchoolMember[]);
            }),
          );
        }

        return this.store.select(
          teacherSelector.getStudentsByGrade({
            gradeId: grade.id!,
            groupId: this.groupId,
            academicYear: this.academicYear ?? this.group?.academicYear ?? null,
          }),
        );
      }),
      takeUntil(this.exit$),
    );

    // Keep the subscription to trigger the "fetch" side effects
    this.grade$.pipe(takeUntil(this.exit$)).subscribe((grade) => {
      if (grade) {
        this.fetchStudentsIfNeeded(grade);
        this.getAttendance();
      } else if (this.gradeId) {
        // Dispatch action to load grade if missing
        this.store.dispatch(
          gradeActions.getGrade({
            gradeId: this.gradeId,
            groupId: this.groupId!,
            gradeYear: this.academicYear ?? this.group?.academicYear!,
          }),
        );
      }
    });

    this.loadingStudents$ = this.store
      .select(memberSelector.loading)
      .pipe(takeUntil(this.exit$));
  }

  /** Viewing an academic year other than the school's current one */
  get isPastYear(): boolean {
    return (
      this.academicYear !== undefined &&
      this.group?.academicYear !== undefined &&
      this.academicYear !== this.group.academicYear
    );
  }

  private fetchStudentsIfNeeded(grade: Grade) {
    if (this.isPastYear) return;
    this.students$.pipe(take(1)).subscribe((students) => {
      const expectedCount = this.group?.gradeStats?.[grade.id!] ?? 0;
      const currentCount = students?.length ?? 0;

      // Fetch if list is empty or incomplete
      if (currentCount === 0 || currentCount < expectedCount) {
        this.store.dispatch(
          teacherActions.getStudents({
            source: 'OverviewGradeComponent :: fetchStudentsIfNeeded',
            groupId: this.groupId!,
            gradeId: grade.id!,
            academicYear: grade.gradeYear ?? this.group?.academicYear,
            status: [MembershipStatus.active],
          }),
        );
      }
    });
  }

  getAttendance() {
    this.grade$
      .pipe(
        take(1),
        map((grade) => {
          if (!this.group || !grade) throw new Error('Missing data');
          return { group: this.group, grade };
        }),
      )
      .subscribe({
        next: async ({ group, grade }) => {
          this.loadingAttendance$ = of(true);
          try {
            const params = {
              groupId: this.groupId!,
              academicYear: group.academicYear!,
              gradeId: grade.id!,
            };
            this.logger.debug('OverviewGradeComponent :: getAttendance', params);
            const attendance =
              await this.attendanceService.getAttendanceByGradeId(params);
            this.logger.debug(
              'OverviewGradeComponent :: getAttendance result',
              attendance?.length ?? 0,
            );

            this.processAttendance(attendance);
          } catch (error) {
            this.logger.error('Attendance load failed', error);
          } finally {
            this.loadingAttendance$ = of(false);
          }
        },
        error: (err) => {
          this.utilsService.onShowAlert(
            {
              title: 'Error Occurred!!',
              message: 'Group or Grade info is missing.',
            },
            false,
          );
        },
      });
  }

  private processAttendance(attendance: AttendanceStats[] | null) {
    this.attendanceMap = {};
    for (const a of attendance ?? []) {
      this.attendanceMap[a.studentId] = attendancePercent(a);
    }
  }

  addMoreStudents() {
    this.grade$
      .pipe(
        take(1),
        map((grade) => {
          if (!this.group || !grade) throw new Error('Missing data');
          return { group: this.group, grade };
        }),
      )
      .subscribe({
        next: async ({ group, grade }) => {
          this.inProgress$ = of(true);
          this.students$.pipe(take(1)).subscribe((students) => {
            this.store.dispatch(
              teacherActions.getStudents({
                source: 'OverviewGradeComponent :: addMoreStudents',
                groupId: group.id!,
                gradeId: grade.id!,
                academicYear: this.academicYear ?? group.academicYear!,
                status: [MembershipStatus.active],
                lastRecord:
                  (students ?? [])?.length > 0
                    ? students![students!.length - 1]
                    : undefined,
              }),
            );
          });
        },
        error: (err) => {
          this.utilsService.onShowAlert(
            {
              title: 'Error Occurred!!',
              message: 'Group or Grade info is missing.',
            },
            false,
          );
        },
      });
  }
}
